import type { Action, Reducer } from 'redux';
import { IApplicationState, IAppThunkAction } from '.';
import IPublicPaymentShare from '../interfaces/IPublicPaymentShare';
import IPaymentGateway from '../interfaces/IPaymentGateway';
import SplitPaymentService from '../service/splitPayments';
import { Routes, navigateTo } from '../helpers/routes';
import { GET_PAYMENT_GATEWAYS_SUCCESS, GET_PAYMENT_GATEWAYS_COMPLETED, actionCreators as PaymentActions } from './Payments';
import { actionCreators as UIActions } from './UI';
import { appInsights } from '../Logging/appInsights';

export type SplitPaymentLandingErrorType = 'expired' | 'paid' | 'notfound' | 'generic' | undefined;

export interface ISplitPaymentLandingState {
    share: IPublicPaymentShare | undefined;
    loading: boolean;
    error: boolean;
    errorType: SplitPaymentLandingErrorType;
    voucherLoading: boolean;
    voucherError: boolean;
    appliedVoucherCode: string | undefined;
    allocationLoading: boolean;
    allocationError: boolean;
    declined: boolean;
}

const FETCH_SHARE_REQUESTED: 'FETCH_SHARE_REQUESTED' = 'FETCH_SHARE_REQUESTED';
export const FETCH_SHARE_SUCCESS: 'FETCH_SHARE_SUCCESS' = 'FETCH_SHARE_SUCCESS';
const FETCH_SHARE_FAILED: 'FETCH_SHARE_FAILED' = 'FETCH_SHARE_FAILED';
const SHARE_VOUCHER_LOADING: 'SHARE_VOUCHER_LOADING' = 'SHARE_VOUCHER_LOADING';
const SHARE_VOUCHER_APPLIED: 'SHARE_VOUCHER_APPLIED' = 'SHARE_VOUCHER_APPLIED';
const SHARE_VOUCHER_REMOVED: 'SHARE_VOUCHER_REMOVED' = 'SHARE_VOUCHER_REMOVED';
const SHARE_VOUCHER_FAILED: 'SHARE_VOUCHER_FAILED' = 'SHARE_VOUCHER_FAILED';
const ALLOCATION_LOADING: 'ALLOCATION_LOADING' = 'ALLOCATION_LOADING';
const ALLOCATION_UPDATED: 'ALLOCATION_UPDATED' = 'ALLOCATION_UPDATED';
const ALLOCATION_FAILED: 'ALLOCATION_FAILED' = 'ALLOCATION_FAILED';
const SHARE_DECLINED: 'SHARE_DECLINED' = 'SHARE_DECLINED';

interface IFetchShareRequestedAction extends Action {
    type: typeof FETCH_SHARE_REQUESTED;
}

interface IFetchShareSuccessAction extends Action {
    type: typeof FETCH_SHARE_SUCCESS;
    share: IPublicPaymentShare;
}

interface IFetchShareFailedAction extends Action {
    type: typeof FETCH_SHARE_FAILED;
    errorType: SplitPaymentLandingErrorType;
}

interface IShareVoucherLoadingAction extends Action {
    type: typeof SHARE_VOUCHER_LOADING;
}

interface IShareVoucherAppliedAction extends Action {
    type: typeof SHARE_VOUCHER_APPLIED;
    share: IPublicPaymentShare;
    code: string;
}

interface IShareVoucherRemovedAction extends Action {
    type: typeof SHARE_VOUCHER_REMOVED;
    share: IPublicPaymentShare;
}

interface IShareVoucherFailedAction extends Action {
    type: typeof SHARE_VOUCHER_FAILED;
}

interface IAllocationLoadingAction extends Action {
    type: typeof ALLOCATION_LOADING;
}

interface IAllocationUpdatedAction extends Action {
    type: typeof ALLOCATION_UPDATED;
    share: IPublicPaymentShare;
}

interface IAllocationFailedAction extends Action {
    type: typeof ALLOCATION_FAILED;
}

interface IShareDeclinedAction extends Action {
    type: typeof SHARE_DECLINED;
}

export type KnownSplitPaymentLandingActions =
    | IFetchShareRequestedAction
    | IFetchShareSuccessAction
    | IFetchShareFailedAction
    | IShareVoucherLoadingAction
    | IShareVoucherAppliedAction
    | IShareVoucherRemovedAction
    | IShareVoucherFailedAction
    | IAllocationLoadingAction
    | IAllocationUpdatedAction
    | IAllocationFailedAction
    | IShareDeclinedAction;

export const actionCreators = {
    fetchShare:
        (shareToken: string): IAppThunkAction<any> =>
        (dispatch) => {
            dispatch({ type: FETCH_SHARE_REQUESTED });

            SplitPaymentService.GetPublicShare(shareToken)
                .then((share) => {
                    // Check if share is already paid or expired
                    if (share.isExpired) {
                        dispatch({ type: FETCH_SHARE_FAILED, errorType: 'expired' as SplitPaymentLandingErrorType });
                        return;
                    }

                    if (share.paid >= share.total) {
                        dispatch({ type: FETCH_SHARE_FAILED, errorType: 'paid' as SplitPaymentLandingErrorType });
                        return;
                    }

                    dispatch({ type: FETCH_SHARE_SUCCESS, share });

                    // Dispatch payment gateway data into the payments slice
                    const gatewayData: IPaymentGateway[] = share.paymentGateways.map((gw) => ({
                        apiKey: gw.apiKey,
                        gateway: gw.gateway,
                        paymentMethodType: gw.paymentMethodType,
                    }));
                    dispatch({ type: GET_PAYMENT_GATEWAYS_SUCCESS, data: gatewayData });
                    dispatch({ type: GET_PAYMENT_GATEWAYS_COMPLETED });
                })
                .catch((error: Error) => {
                    const errorType: SplitPaymentLandingErrorType =
                        error.name?.includes('404') ? 'notfound' : 'generic';
                    dispatch({ type: FETCH_SHARE_FAILED, errorType });
                    appInsights?.trackException(
                        { error: new Error('Error fetching public share'), severityLevel: 2 },
                        { exception: error, sourceFile: 'SplitPaymentLanding.ts', product: 'booking' }
                    );
                });
        },

    checkShareStatus:
        (shareToken: string): IAppThunkAction<any> =>
        (dispatch, getState) => {
            SplitPaymentService.GetPublicShare(shareToken)
                .then((share) => {
                    const appState: IApplicationState = getState();
                    const currentShare = appState.splitPaymentLanding.share;

                    // Only dispatch if the paid amount has changed
                    if (!currentShare || currentShare.paid !== share.paid) {
                        dispatch({ type: FETCH_SHARE_SUCCESS, share });
                    }
                })
                .catch((error: Error) => {
                    if (!error.name?.includes('404')) {
                        appInsights?.trackException(
                            { error: new Error('Error polling share status'), severityLevel: 4 },
                            { exception: error, sourceFile: 'SplitPaymentLanding.ts', product: 'booking' }
                        );
                    }
                });
        },

    confirmSharePaymentSuccess:
        (): IAppThunkAction<any> =>
        (dispatch) => {
            dispatch(navigateTo(Routes.Complete));
        },

    confirmSharePaypal:
        (
            shareToken: string,
            paypalOrderId: string,
            paypalCaptureId: string,
            payerEmail?: string,
            payerName?: string,
            paypalState?: string
        ): IAppThunkAction<any> =>
        (dispatch) => {
            SplitPaymentService.ConfirmSharePaypal(shareToken, {
                paypalOrderId,
                paypalCaptureId,
                payerEmail,
                payerName,
                paypalState,
            })
                .then((share) => {
                    dispatch({ type: FETCH_SHARE_SUCCESS, share });
                    dispatch(PaymentActions.paypalPaymentSuccess());
                })
                .catch((error: Error) => {
                    appInsights?.trackException(
                        { error: new Error('Error confirming share paypal payment'), severityLevel: 2 },
                        {
                            exception: error,
                            shareToken,
                            paypalOrderId,
                            sourceFile: 'SplitPaymentLanding.ts',
                            product: 'booking',
                        }
                    );
                    dispatch(UIActions.displayError('errors.payments.error'));
                    dispatch(PaymentActions.paypalPaymentError());
                });
        },

    applyVoucher:
        (shareToken: string, code: string): IAppThunkAction<any> =>
        (dispatch) => {
            dispatch({ type: SHARE_VOUCHER_LOADING });

            SplitPaymentService.ApplyShareVoucher(shareToken, code)
                .then((share) => {
                    dispatch({ type: SHARE_VOUCHER_APPLIED, share, code });
                })
                .catch((error: Error) => {
                    dispatch({ type: SHARE_VOUCHER_FAILED });
                    appInsights?.trackException(
                        { error: new Error('Error applying share voucher'), severityLevel: 2 },
                        { exception: error, shareToken, sourceFile: 'SplitPaymentLanding.ts', product: 'booking' }
                    );
                });
        },

    removeVoucher:
        (shareToken: string, code: string): IAppThunkAction<any> =>
        (dispatch) => {
            dispatch({ type: SHARE_VOUCHER_LOADING });

            SplitPaymentService.RemoveShareVoucher(shareToken, code)
                .then((share) => {
                    dispatch({ type: SHARE_VOUCHER_REMOVED, share });
                })
                .catch((error: Error) => {
                    dispatch({ type: SHARE_VOUCHER_FAILED });
                    appInsights?.trackException(
                        { error: new Error('Error removing share voucher'), severityLevel: 2 },
                        { exception: error, shareToken, sourceFile: 'SplitPaymentLanding.ts', product: 'booking' }
                    );
                });
        },

    updateAllocation:
        (shareToken: string, slotCount: number): IAppThunkAction<any> =>
        (dispatch) => {
            dispatch({ type: ALLOCATION_LOADING });

            SplitPaymentService.UpdateShareAllocation(shareToken, slotCount)
                .then((share) => {
                    dispatch({ type: ALLOCATION_UPDATED, share });
                    // The amount due changed, so any pending payment intent is stale.
                    dispatch(PaymentActions.clearClientSecret());
                })
                .catch((error: Error) => {
                    dispatch({ type: ALLOCATION_FAILED });
                    appInsights?.trackException(
                        { error: new Error('Error updating share allocation'), severityLevel: 2 },
                        { exception: error, shareToken, sourceFile: 'SplitPaymentLanding.ts', product: 'booking' }
                    );
                });
        },

    declineShare:
        (shareToken: string): IAppThunkAction<any> =>
        (dispatch) => {
            dispatch({ type: ALLOCATION_LOADING });

            SplitPaymentService.DeclineShare(shareToken)
                .then(() => {
                    dispatch({ type: SHARE_DECLINED });
                })
                .catch((error: Error) => {
                    dispatch({ type: ALLOCATION_FAILED });
                    appInsights?.trackException(
                        { error: new Error('Error declining share'), severityLevel: 2 },
                        { exception: error, shareToken, sourceFile: 'SplitPaymentLanding.ts', product: 'booking' }
                    );
                });
        },

    confirmShareNoPayment:
        (shareToken: string): IAppThunkAction<any> =>
        (dispatch) => {
            SplitPaymentService.ConfirmShareNoPayment(shareToken)
                .then((share) => {
                    dispatch({ type: FETCH_SHARE_SUCCESS, share });
                    dispatch(navigateTo(Routes.Complete));
                })
                .catch((error: Error) => {
                    appInsights?.trackException(
                        { error: new Error('Error confirming share with no payment'), severityLevel: 2 },
                        { exception: error, shareToken, sourceFile: 'SplitPaymentLanding.ts', product: 'booking' }
                    );
                    dispatch(UIActions.displayError('errors.payments.error'));
                });
        },
};

const unloadedState: ISplitPaymentLandingState = {
    share: undefined,
    loading: false,
    error: false,
    errorType: undefined,
    voucherLoading: false,
    voucherError: false,
    appliedVoucherCode: undefined,
    allocationLoading: false,
    allocationError: false,
    declined: false,
};

export const reducer: Reducer<ISplitPaymentLandingState> = (
    state: ISplitPaymentLandingState | undefined,
    incomingAction: Action
): ISplitPaymentLandingState => {
    if (state === undefined) return unloadedState;

    const action = incomingAction as KnownSplitPaymentLandingActions;
    switch (action.type) {
        case FETCH_SHARE_REQUESTED:
            return {
                ...state,
                loading: true,
                error: false,
                errorType: undefined,
            };
        case FETCH_SHARE_SUCCESS:
            return {
                ...state,
                loading: false,
                share: action.share,
                error: false,
                errorType: undefined,
            };
        case FETCH_SHARE_FAILED:
            return {
                ...state,
                loading: false,
                error: true,
                errorType: action.errorType,
            };
        case SHARE_VOUCHER_LOADING:
            return {
                ...state,
                voucherLoading: true,
                voucherError: false,
            };
        case SHARE_VOUCHER_APPLIED:
            return {
                ...state,
                voucherLoading: false,
                voucherError: false,
                share: action.share,
                appliedVoucherCode: action.code,
            };
        case SHARE_VOUCHER_REMOVED:
            return {
                ...state,
                voucherLoading: false,
                voucherError: false,
                share: action.share,
                appliedVoucherCode: undefined,
            };
        case SHARE_VOUCHER_FAILED:
            return {
                ...state,
                voucherLoading: false,
                voucherError: true,
            };
        case ALLOCATION_LOADING:
            return {
                ...state,
                allocationLoading: true,
                allocationError: false,
            };
        case ALLOCATION_UPDATED:
            return {
                ...state,
                allocationLoading: false,
                allocationError: false,
                share: action.share,
            };
        case ALLOCATION_FAILED:
            return {
                ...state,
                allocationLoading: false,
                allocationError: true,
            };
        case SHARE_DECLINED:
            return {
                ...state,
                allocationLoading: false,
                allocationError: false,
                declined: true,
            };
        default:
            return state;
    }
};
